<template>
  <div class="wt2-sg wt2-layout-picker">
    <div class="-categories">
      <div class="wt2-text-xl -title">Layouts</div>
      <div class="-inner" :data-ar="aspectRatioName">
        <div
          v-for="c in categories"
          :key="c.category + key"
          class="-category"
          :class="{
            '-selected':
              selectedCategory && c.category === selectedCategory.category,
          }"
          @click="selectCategory(c)"
        >
          <div class="-thumbnail">
            <overlay-thumbnail
              :overlay="getSampleAsset(c)"
              :aspect-ratio="aspectRatio"
              :trim-curtains="true"
              :key="selectedOverlayAsset.guid"
            />
          </div>
          <div
            class="wt2-text-s -category-caption"
            v-text="getCategoryTitle(c.category)"
          />
        </div>
      </div>
    </div>

    <div v-if="showDesignSection" class="-design">
      <template v-if="windows.length > 1">
        <div class="-ratio wt2-text-n">Composition</div>
        <div class="-windows">
          <div
            v-for="w in windows"
            :key="w.sceneLayoutWindow"
            class="-window"
            :class="{
              '-selected':
                selectedWindow &&
                w.sceneLayoutWindow === selectedWindow.sceneLayoutWindow,
            }"
            @click="selectWindow(w)"
            :data-id="w.sceneLayoutWindow"
          >
            <div class="-icon">
              <i class="wticon" :class="getWindowIcon(w.sceneLayoutWindow)" />
            </div>
            <div class="wt2-text-s -window-caption">
              {{ getWindowTitle(w.sceneLayoutWindow) }}
            </div>
          </div>
        </div>
      </template>

      <div v-if="isPip" class="-position">
        <span>Position</span>
        <position-picker
          :selected-position="selectedOverlayAsset.sceneLayout.layoutPosition"
          @selected="selectLayout"
        />
      </div>
      <div v-if="hasBorderPresets" class="-border-presets">
        <div class="wt2-text-n -subtitle">
          {{ borderTitle }}
          <span
            v-if="isPip"
            :key="isPip"
            class="wticon wticon-information1"
            v-tooltip="borderTooltip"
          />
          <span
            v-else
            :key="isPip"
            class="wticon wticon-information1"
            v-tooltip="splitLineTooltip"
          />
        </div>
        <wt-radio-text
          :buttons="borderPresets"
          :value="currentBorder"
          @select="$_setBorder"
        />
      </div>
    </div>

    <collage-custom-overlays />

    <div class="-overlay-asset-transitions">
      <div class="wt2-text-n -subtitle">
        In & Out Animation
        <span
          class="wticon wticon-information1"
          v-tooltip="transitionsTooltip"
        />
      </div>

      <wt-radio-text
        :buttons="transitionOptions"
        :value="currentTransition"
        @select="$_setTransitions"
      />
    </div>
  </div>
</template>

<script>
import app from 'app';
import PositionPicker from './PositionPicker.vue';
import OverlayThumbnail from 'components/thumbnail/OverlayThumbnail.vue';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import PictureInPictureAdapter from 'c/PictureInPictureAdapter';
import { mapActions, mapGetters } from 'vuex';
import CollageCustomOverlays from 'AssetEditor/functionality/OverlayAsset/CollageCustomOverlays.vue';

export default {
  components: {
    PositionPicker,
    OverlayThumbnail,
    CollageCustomOverlays,
  },

  data() {
    return {
      key: 0,
      selectedCategory: null,
      selectedWindow: null,
      aspectRatio: app.ASPECT_RATIOS[app.currentAspectRatio].ratio,
      aspectRatioName: app.currentAspectRatio,
      animationAvailability: {
        canAnimateAtLeft: true,
        canAnimateAtRight: true,
      },
    };
  },

  beforeDestroy() {
    bus.$off('refreshOverlayRCP', this.updateKey);
    this.selectedCategory.lastWindow = null;
    this.selectedCategory.lastLayout = null;
  },

  mounted() {
    bus.$on('refreshOverlayRCP', this.updateKey);
    this.init();
  },

  watch: {
    selectedOverlayAsset() {
      this.init();
    },
  },

  methods: {
    ...mapActions('assetEditor', [
      'selectOverlayAssetLayout',
      'setBorder',
      'setTransitions',
    ]),

    init() {
      const { layout, window, category } = this.getLayoutById(
        this.selectedOverlayAsset.sceneLayout.id
      );
      [
        'canSwitchMainAndOverlay',
        'borderPresets',
        'animationPresets',
        'layoutPosition',
        'layoutWindow',
      ].forEach((p) =>
        app.Vue.set(this.selectedOverlayAsset.sceneLayout, p, layout[p])
      );
      this.selectedWindow = window;
      this.selectedCategory = category;
      app.Vue.set(
        this,
        'animationAvailability',
        PictureInPictureAdapter.checkAnimationAvailability({
          scene: this.workModel,
          overlay: this.selectedOverlayAsset,
        })
      );
    },

    getLayoutById(id) {
      for (const { layout, window, category } of this.layouts()) {
        if (layout.id === id) {
          return { layout, window, category };
        }
      }
    },

    *layouts() {
      for (const category of this.categories) {
        for (const window of category.sceneWindows) {
          for (const layout of window.layouts) {
            yield { category, window, layout };
          }
        }
      }
    },

    getSampleAsset(category) {
      const clone = _.cloneDeepAndClean(this.selectedOverlayAsset);
      const { layout } = this.getLayoutById(
        this.defaultLayoutMap[category.category]
      );
      clone.sceneLayout = _.cloneDeepAndClean(layout);
      clone.sceneLayout.switchMainAndOverlay = false;
      return clone;
    },

    selectCategory(c) {
      app.analytics('analytics.scenes.compositionChanged', {
        changedProperty: 'Category',
        newValue: c.category,
        oldValue: this.selectedCategory.category,
      });

      if (this.selectedCategory) {
        this.selectedCategory.lastWindow = this.selectedWindow;
        this.selectedCategory.lastLayout =
          this.selectedOverlayAsset.sceneLayout;
      }

      this.selectedCategory = c;
      const defaultLayout = _.chain(c.sceneWindows)
        .map((sw) => sw.layouts)
        .flatten()
        .find((l) => l.id === this.defaultLayoutMap[c.category])
        .value();
      if (c && c.lastWindow) {
        this.selectedWindow = c.lastWindow;
      } else {
        this.selectedWindow = _.find(c.sceneWindows, (sw) =>
          sw.layouts.find((l) => l.id === defaultLayout.id)
        );
      }

      let newLayout;

      if (c && c.lastLayout) {
        newLayout = c.lastLayout;
      } else {
        newLayout = defaultLayout;
      }
      this.selectOverlayAssetLayout({ layout: newLayout });
    },

    selectWindow(w) {
      app.analytics('analytics.scenes.compositionChanged', {
        changedProperty: 'Composition',
        newValue: this.getWindowTitle(w.sceneLayoutWindow),
        oldValue: this.getWindowTitle(this.selectedWindow.sceneLayoutWindow),
      });

      this.selectedWindow = w;
      if (this.selectedLayout) {
        const currentPosition = this.selectedLayout.layoutPosition;
        this.selectLayout(currentPosition);
      }
    },

    selectLayout(position) {
      let newSelectedLayout = this.selectedWindow.layouts.find(
        (layout) => layout.layoutPosition === position
      );
      if (!newSelectedLayout) {
        newSelectedLayout = this.selectedWindow.layouts[0];
      }

      this.selectOverlayAssetLayout({
        layout: newSelectedLayout,
        oldLayout: this.selectedLayout,
      });

      if (position !== this.selectedLayout.layoutPosition) {
        app.analytics('analytics.scenes.compositionChanged', {
          changedProperty: 'Position',
          newValue: position,
          oldValue: this.selectedLayout.layoutPosition,
        });
      }
    },

    getCategoryTitle(id) {
      return app.culture.get('overlayAssets.categories.' + id);
    },

    getWindowTitle(id) {
      return app.culture.get('overlayAssets.windows.' + id);
    },

    getWindowIcon(id) {
      id = id || 'WIDE';
      const iconMap = {
        WIDE: 'wticon-wide',
        VERTICAL: 'wticon-vertical',
        SQUARE: 'wticon-square1',
        SPLIT: 'wticon-split_middle',
        SPLIT_2_3: 'wticon-split_left',
        SPLIT_1_3: 'wticon-split_right',
        SPLIT_VERTICAL: 'wticon-vertical1',
        SPLIT_VERTICAL_1_3: 'wticon-vertical2',
        SPLIT_VERTICAL_2_3: 'wticon-vertical3',
        BRANDED: 'wticon-vertical1',
      };
      return iconMap[id];
    },

    $_setBorder(borderId) {
      const currentBorder = this.selectedLayout.borderPresets.find(
        (p) => p.id === this.currentBorder
      );
      const newBorder = this.selectedLayout.borderPresets.find(
        (p) => p.id === borderId
      );

      app.analytics('analytics.scenes.compositionChanged', {
        changedProperty: this.borderTitle,
        newValue: newBorder.name,
        oldValue: currentBorder.name,
      });

      this.setBorder(newBorder);
    },

    $_setTransitions(transitionId) {
      const currentTransitionText = this.transitionOptions.find(
        (o) => o.value === this.currentTransition
      ).text;
      const newTransitionText = this.transitionOptions.find(
        (o) => o.value === transitionId
      ).text;
      app.analytics('analytics.scenes.compositionChanged', {
        changedProperty: 'Animation',
        newValue: newTransitionText,
        oldValue: currentTransitionText,
      });

      const [inAnimation, outAnimation] = transitionId.split(':');
      this.setTransitions({ inAnimation, outAnimation });
    },

    updateKey() {
      this.key++;
    },
  },

  computed: {
    ...mapGetters('assetEditor', [
      'themeInfo',
      'currentPage',
      'selectedOverlayAsset',
      'selectedLayout',
      'workModel',
    ]),

    showDesignSection() {
      return this.windows.length > 1 || this.isPip || this.hasBorderPresets;
    },

    borderTitle() {
      return this.isPip ? 'Border' : 'Split Line';
    },

    isPip() {
      return (
        this.selectedCategory &&
        this.selectedCategory.category === 'PICTURE_IN_PICTURE'
      );
    },

    categories() {
      return this.themeInfo.sceneLayouts.layoutCategories.filter(
        (c) => c.sceneWindows.length > 0
      );
    },

    defaultLayoutMap() {
      return this.themeInfo.sceneLayouts.defaultLayoutMap;
    },

    windows() {
      if (!this.selectedCategory) {
        return [];
      }
      return this.selectedCategory.sceneWindows;
    },

    hasBorderPresets() {
      return (
        this.selectedLayout &&
        this.selectedLayout.borderPresets &&
        this.selectedLayout.borderPresets.length
      );
    },

    borderPresets() {
      if (this.hasBorderPresets) {
        return this.selectedLayout.borderPresets.map((p) => ({
          value: p.id,
          text: p.name,
        }));
      }
      return [];
    },

    currentBorder() {
      if (this.selectedLayout.assetBorder) {
        return this.selectedLayout.assetBorder.id;
      }
      return null;
    },

    currentTransition() {
      return (
        this.selectedLayout.inAnimationId +
        ':' +
        this.selectedLayout.outAnimationId
      );
    },

    transitionId() {
      if (
        this.selectedLayout &&
        this.selectedLayout.animationPresets &&
        this.selectedLayout.animationPresets.length
      ) {
        const notCut = this.selectedLayout.animationPresets.find(
          (anim) => anim.animationId !== 'CUT'
        );
        if (notCut) {
          return notCut.animationId;
        }
      }
      return 'FADE';
    },

    transitionOptions() {
      const onlyIn = this.transitionId + ':CUT';
      const onlyOut = 'CUT:' + this.transitionId;
      const both = this.transitionId + ':' + this.transitionId;
      const none = 'CUT:CUT';
      return [
        { value: none, text: 'None' },
        {
          value: onlyIn,
          text: 'Only In',
          disabled: !this.animationAvailability.canAnimateAtLeft,
        },
        {
          value: onlyOut,
          text: 'Only Out',
          disabled: !this.animationAvailability.canAnimateAtRight,
        },
        {
          value: both,
          text: 'In & Out',
          disabled:
            !this.animationAvailability.canAnimateAtLeft ||
            !this.animationAvailability.canAnimateAtRight,
        },
      ];
    },

    transitionsTooltip() {
      return {
        title:
          'Animation is applied on the Preview of your collage. Click Apply to see it.',
        placement: 'right',
      };
    },

    borderTooltip() {
      return {
        title:
          'Border is applied on the Preview of your collage. Click Apply to see it.',
        placement: 'right',
      };
    },

    splitLineTooltip() {
      return {
        title:
          'Split Line is applied on the Preview of your collage. Click Apply to see it.',
        placement: 'right',
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.-category-caption {
  white-space: normal;
}
</style>
